import { IonButton, IonInput, IonItem } from '@ionic/react';
import { useFormik } from 'formik';
import i18next from 'i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useMutateRegisterTransfer } from '../../../api/hooks/delivery/useMutateRegisterTransfer';
import { Loading } from '../../../components/atoms/Loading';
import { DeliveryStatusByQrUrl } from './DeliveryStatusByQrUrl';
import { ProviderInvoiceDoc } from './ProviderInvoiceDoc';
import { BSelect } from '../../../components/molecules/BSelect';
import { QrScanButton } from '../../../components/molecules/QrScanButton';
import { TEST_IDS } from '../../../config/utils/constants';
import { disableSubmitOnEnter } from '../../../config/utils/formUtils';
import { TransportType } from '../../../interfaces/myDataDelivery.interface';
import { getTransportTypeOptions } from '../../../services/utils/deliveryStatusUtils';

interface Props {
  mark: string | number;
  qrUrl: string;
}

const validationSchema = yup.object().shape({
  mark: yup.string().trim(),
  qrUrl: yup.string().trim().required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
  vehicleNumber: yup
    .string()
    .trim()
    .required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD'))
    .max(50, i18next.t('FORMS.ERRORS.MAX_LENGTH', { max: 50 })),
  transportType: yup.mixed().required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
  pNumber: yup.string().trim().nullable(),
});

const sanitizeTransportType = (val: any): number | undefined => {
  if (val === null || val === undefined) return undefined;

  // BSelect commonly stores the selection as an object: { value: 1, label: '...' } or { id: 1, name: '...' }
  if (typeof val === 'object') {
    // prefer `value`, fallback to `id`, fallback to numeric coercion
    const candidate = val.value ?? val.id ?? val;
    const asNum = Number(candidate);
    return Number.isNaN(asNum) ? undefined : asNum;
  }

  // if it's a primitive (number or string)
  const asNum = Number(val);
  return Number.isNaN(asNum) ? undefined : asNum;
};

export const DeliveryRegisterTransferForm: React.FC<Props> = ({ mark, qrUrl }) => {
  const { t } = useTranslation();
  const { mutate, isLoading } = useMutateRegisterTransfer();
  const [committedQrUrl, setCommittedQrUrl] = useState<string | undefined>(qrUrl || undefined);
  const transportOptions = useMemo(() => getTransportTypeOptions(), []);
  const transportSelectOptions = useMemo(
    () => transportOptions.map((o) => ({ id: o.value, name: t(o.label) })),
    [transportOptions, t],
  );
  const initialTransport =
    transportSelectOptions.find((o) => o.id === TransportType.OTHER) || transportSelectOptions[0];
  const initialValues = useMemo(
    () => ({
      mark,
      qrUrl,
      vehicleNumber: '',
      transportType: initialTransport,
      pNumber: '',
      latitude: undefined,
      longitude: undefined,
    }),
    [initialTransport, mark, qrUrl],
  );

  const getLoading = useCallback(() => {
    if (isLoading) {
      return <Loading global />;
    }

    return null;
  }, [isLoading]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      const data = {
        ...values,
        mark: values.mark || undefined,
        pNumber: values.pNumber || undefined,
        latitude: values.latitude || undefined,
        longitude: values.longitude || undefined,
        transportType: sanitizeTransportType(values.transportType) as TransportType,
      };

      mutate(data, {
        onSuccess: () => {
          setCommittedQrUrl(data.qrUrl);
          // Reset back to our object-shaped initial values so BSelect shows the selected option
          resetForm({ values: initialValues });
        },
      });
    },
  });

  useEffect(() => {
    if (sanitizeTransportType(formik.values.transportType) === TransportType.NONE) {
      formik.setFieldValue('vehicleNumber', t('DELIVERY.TRANSPORT_TYPE_NONE_C'));
    }
  }, [formik?.values?.transportType, t]);

  const getMarkQrFields = useCallback(() => {
    if (!qrUrl) {
      return (
        <div>
          <div className="p-b-12 p-l-6 flex-start-center-row-nowrap gap-6">
            {t('DELIVERY.QR_TITLE')}
            <QrScanButton onScan={(url) => formik.setFieldValue('qrUrl', url)} />
          </div>
          <IonItem
            className={`width-300px height-50px border-radius-input g-select-border ${
              formik.touched.qrUrl && formik.errors.qrUrl ? 'b-field-border-error' : ''
            }`}
            mode="md">
            <IonInput
              className="height-48px"
              mode="md"
              type="text"
              name="qrUrl"
              placeholder={t('DELIVERY.QR_PLACEHOLDER') + '...'}
              onIonInput={(e: any) => formik.setFieldValue('qrUrl', e?.detail?.value)}
              onIonBlur={() => {
                formik.setFieldTouched('qrUrl', true);
                setCommittedQrUrl(formik.values.qrUrl || undefined);
              }}
              value={formik.values.qrUrl}
              onKeyPress={disableSubmitOnEnter}
            />
          </IonItem>
        </div>
      );
    }
  }, [formik, qrUrl, t]);

  return (
    <>
      {getLoading()}
      <div>
        <form onSubmit={formik.handleSubmit}>
          <div className="simpleBorder p-12">
            <div className="boldText5 margin-b-24 text-center">{t('DELIVERY.REGISTER_TRANSFER_TITLE')}</div>

            <div className="flex-center-center-row-wrap gap-12 p-b-12">
              {getMarkQrFields()}
              <div>
                <div className="p-b-12 p-l-6">{t('DELIVERY.VEHICLE_NUMBER')}</div>
                <IonItem
                  className={`width-300px height-50px border-radius-input g-select-border ${
                    formik.touched.vehicleNumber && formik.errors.vehicleNumber ? 'b-field-border-error' : ''
                  }`}
                  mode="md">
                  <IonInput
                    className="height-48px"
                    mode="md"
                    type="text"
                    name="vehicleNumber"
                    placeholder={t('DELIVERY.VEHICLE_NUMBER_PLACEHOLDER') + '...'}
                    onIonInput={(e: any) => formik.setFieldValue('vehicleNumber', e?.detail?.value)}
                    onIonBlur={() => formik.setFieldTouched('vehicleNumber', true)}
                    value={formik.values.vehicleNumber}
                    onKeyPress={disableSubmitOnEnter}
                  />
                </IonItem>
              </div>

              <div>
                <BSelect
                  field={{
                    name: 'transportType',
                    label: 'TRANSPORT_TYPE',
                    placeholder: 'TRANSPORT_TYPE',
                    allowClear: false,
                    labelStyle: 'p-b-12 p-l-6',
                    data: transportSelectOptions,
                  }}
                  formik={formik}
                  isRequired={true}
                  widthClass="width-300px"
                  testId={TEST_IDS.Delivery.transportTypeSelect}
                />
              </div>

              <div>
                <div className="p-b-12 p-l-6">{t('DELIVERY.P_NUMBER')}</div>
                <IonItem className="width-300px border-radius-input g-select-border" mode="md">
                  <IonInput
                    className="height-48px"
                    mode="md"
                    type="text"
                    name="pNumber"
                    placeholder={t('DELIVERY.P_NUMBER_PLACEHOLDER')}
                    onIonInput={(e: any) => formik.setFieldValue('pNumber', e?.detail?.value)}
                    onIonBlur={() => formik.setFieldTouched('pNumber', true)}
                    value={formik.values.pNumber}
                    onKeyPress={disableSubmitOnEnter}
                  />
                </IonItem>
              </div>
            </div>
          </div>

          <div className="flex-center-center-column-nowrap m-t-30 width-100">
            <div className="max-width-460px">
              <IonButton
                mode="md"
                type="submit"
                disabled={!(formik.isValid && formik.dirty) || isLoading}
                className="button-focused width-100">
                {t('DELIVERY.REGISTER_TRANSFER_SUBMIT')}
              </IonButton>
            </div>
          </div>
        </form>
        <ProviderInvoiceDoc qrUrl={formik.values.qrUrl} />
        {/* When `mark` is already known, the parent screen already fetched and renders this same
            status/doc data via useDeliveryStatusByMark -- avoid a duplicate qr-status request */}
        {!mark && <DeliveryStatusByQrUrl qrUrl={committedQrUrl} />}
      </div>
    </>
  );
};
